/**
 * ProcessingFunnelChart
 * Gráfico de funil mostrando conversão no processamento de publicações
 *
 * Fase 2.3 do Plano de Evolução - Funil de Conversão
 */

import { Filter, TrendingDown, ChevronRight, Lightbulb } from 'lucide-react'
import { PainelCard, PainelEmpty, PainelStat, PainelStatRow } from './PainelCard'

export interface FunnelStage {
  name: string
  count: number
  percentage: number
  /** Cor sugerida pela API (hex). A tela usa as cores do tema por posição no funil. */
  color: string
}

export interface ProcessingFunnelData {
  stages: FunnelStage[]
  conversionRates: {
    receivedToAnalyzed: number
    analyzedToTasks: number
    receivedToTasks: number
  }
}

interface ProcessingFunnelChartProps {
  data: ProcessingFunnelData
  className?: string
}

// Recebidas → Analisadas → Vinculadas → Tarefas → Arquivadas
const STAGE_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)']

function funnelInsight(data: ProcessingFunnelData, totalReceived: number): string {
  const rate = data.conversionRates.receivedToTasks
  if (rate >= 30) {
    return `Excelente taxa de conversão! Das ${totalReceived} publicações recebidas, ${data.stages[2]?.count || 0} geraram tarefas (${rate}%).`
  }
  if (rate >= 15) {
    return 'Taxa de conversão moderada. Considere revisar o processo de triagem para identificar mais casos relevantes.'
  }
  return 'Taxa de conversão baixa. A maioria das publicações está sendo arquivada sem gerar ações.'
}

export function ProcessingFunnelChart({ data, className = '' }: ProcessingFunnelChartProps) {
  if (!data || !data.stages || data.stages.length === 0) {
    return (
      <PainelCard title="Funil de Processamento" icon={Filter} className={className}>
        <PainelEmpty icon={Filter}>Nenhum dado disponível</PainelEmpty>
      </PainelCard>
    )
  }

  const totalReceived = data.stages[0]?.count || 0

  return (
    <PainelCard
      title="Funil de Processamento"
      icon={Filter}
      className={className}
      action={
        <>
          <TrendingDown className="size-3" aria-hidden="true" />
          <span>Taxa de conversão</span>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {data.stages.map((stage, index) => {
          const prevStage = index > 0 ? data.stages[index - 1] : null
          const conversionFromPrev = prevStage && prevStage.count > 0
            ? Math.round((stage.count / prevStage.count) * 100)
            : 100

          return (
            <div key={stage.name} className="min-w-0">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{stage.name}</span>
                  {index > 0 && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <ChevronRight className="size-3" aria-hidden="true" />
                      {conversionFromPrev}% do anterior
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <span className="text-sm font-semibold tabular-nums text-foreground">{stage.count}</span>
                  <span className="ml-1 text-xs text-muted-foreground">({stage.percentage}%)</span>
                </div>
              </div>

              <div className="relative h-10 overflow-hidden rounded-sm bg-muted">
                <div
                  className="flex h-full items-center px-3 transition-all duration-500"
                  style={{
                    width: `${stage.percentage}%`,
                    backgroundColor: STAGE_COLORS[index % STAGE_COLORS.length],
                    minWidth: stage.percentage > 0 ? '60px' : '0px'
                  }}
                >
                  {stage.percentage > 15 && (
                    <span className="rounded-sm bg-background/80 px-1.5 text-xs font-medium tabular-nums text-foreground">{stage.percentage}%</span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <PainelStatRow className="grid-cols-1 sm:grid-cols-3 md:grid-cols-3">
        <PainelStat
          label="Conversão Geral"
          value={`${data.conversionRates.receivedToTasks}%`}
          tone="primary"
          hint="Recebidas → Tarefas"
        />
        <PainelStat
          label="Taxa de Análise"
          value={`${data.conversionRates.receivedToAnalyzed}%`}
          hint="Foram analisadas"
        />
        <PainelStat
          label="Geração de Tarefas"
          value={`${data.conversionRates.analyzedToTasks}%`}
          hint="Das analisadas"
        />
      </PainelStatRow>

      {totalReceived > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-3 text-sm text-foreground">
          <Lightbulb className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
          <p>
            <span className="font-medium">Insight:</span> {funnelInsight(data, totalReceived)}
          </p>
        </div>
      )}
    </PainelCard>
  )
}
